import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { CATEGORIES, useConsent } from '@/lib/consent';

/**
 * CookiePreferencesModal — opened from the banner "Customize" button or from
 * any "Manage cookies" link (e.g. footer).
 *
 * The four categories live in `CATEGORIES`; only `necessary` is locked on.
 */
export default function CookiePreferencesModal() {
  const {
    consent,
    save,
    acceptAll,
    rejectNonEssential,
    managerOpen,
    closeManager,
  } = useConsent();

  const [draft, setDraft] = useState({
    analytics: !!consent.analytics,
    marketing: !!consent.marketing,
    preferences: !!consent.preferences,
  });

  // Sync draft whenever the modal is (re)opened or the underlying consent changes.
  useEffect(() => {
    if (managerOpen) {
      setDraft({
        analytics: !!consent.analytics,
        marketing: !!consent.marketing,
        preferences: !!consent.preferences,
      });
    }
  }, [managerOpen, consent.analytics, consent.marketing, consent.preferences]);

  function set(key, val) {
    setDraft((d) => ({ ...d, [key]: !!val }));
  }

  function handleSave() {
    save(draft);
    toast.success('Cookie preferences saved.');
    closeManager();
  }

  function handleAcceptAll() {
    acceptAll();
    toast.success('All cookies accepted.');
    closeManager();
  }

  function handleReject() {
    rejectNonEssential();
    toast.success('Non-essential cookies rejected.');
    closeManager();
  }

  return (
    <Dialog open={managerOpen} onOpenChange={(o) => !o && closeManager()}>
      <DialogContent
        className="
          bg-gradient-to-b from-[#0B172A]/95 to-[#050B17]/95
          border border-[#7DDCFF]/25
          text-white
          backdrop-blur-xl
          rounded-2xl
          shadow-[0_24px_60px_rgba(0,0,0,0.55),0_0_42px_rgba(34,211,238,0.18)]
          max-w-[600px]
        "
        data-testid="cookie-preferences-modal"
      >
        {/* Top edge gradient line */}
        <div
          aria-hidden="true"
          className="absolute top-0 left-0 right-0 h-px rounded-t-2xl"
          style={{
            background:
              'linear-gradient(90deg, transparent, rgba(34,211,238,0.65), rgba(124,58,237,0.65), transparent)',
          }}
        />
        <DialogHeader className="text-left">
          <DialogTitle className="font-display text-[20px] sm:text-[22px] font-bold tracking-[-0.02em]">
            Manage Cookie Preferences
          </DialogTitle>
          <DialogDescription className="text-slate-300/90 text-[13.5px] leading-relaxed">
            Choose which categories of cookies Ads Dashbound may use. Necessary
            cookies are always on. Read more in the{' '}
            <Link
              to="/cookie-policy"
              onClick={closeManager}
              className="text-[#7DDCFF] hover:text-white underline-offset-4 hover:underline transition-colors"
            >
              Cookie Policy
            </Link>
            .
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-3">
          {CATEGORIES.map((cat) => {
            const isOn =
              cat.locked
                ? true
                : !!draft[cat.key];
            return (
              <div
                key={cat.key}
                data-testid={`cookie-category-${cat.key}`}
                className="
                  flex items-start gap-4 rounded-xl
                  bg-white/[0.04] border border-white/10
                  p-4 backdrop-blur-md
                  transition-colors
                  hover:border-[#7DDCFF]/30
                "
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-[14.5px] font-semibold tracking-[-0.01em]">
                      {cat.title}
                    </h3>
                    {cat.locked && (
                      <span
                        className="
                          inline-flex items-center gap-1
                          px-1.5 py-0.5 rounded-full
                          bg-[#22D3EE]/15 border border-[#22D3EE]/30
                          text-[10px] font-semibold uppercase tracking-wider
                          text-[#7DDCFF]
                        "
                        aria-hidden="true"
                      >
                        <Lock className="h-2.5 w-2.5" /> Always on
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-[12.5px] text-slate-400/90 leading-relaxed">
                    {cat.description}
                  </p>
                </div>
                <Switch
                  checked={isOn}
                  disabled={cat.locked}
                  onCheckedChange={(v) => set(cat.key, v)}
                  aria-label={`${cat.title} toggle`}
                  data-testid={`cookie-toggle-${cat.key}`}
                  className="
                    data-[state=checked]:bg-[#22D3EE]
                    data-[state=unchecked]:bg-white/15
                  "
                />
              </div>
            );
          })}
        </div>

        <DialogFooter className="mt-2 flex-col-reverse sm:flex-row gap-2">
          <Button
            onClick={handleReject}
            variant="ghost"
            className="text-slate-300 hover:text-white hover:bg-white/10 rounded-xl"
            data-testid="cookie-modal-reject"
          >
            Reject Non-Essential
          </Button>
          <Button
            onClick={handleAcceptAll}
            variant="outline"
            className="bg-white/[0.04] text-white border-white/15 hover:bg-white/10 hover:text-white rounded-xl backdrop-blur-md"
            data-testid="cookie-modal-accept-all"
          >
            Accept All
          </Button>
          <Button
            onClick={handleSave}
            className="btn-primary-gradient text-white rounded-xl px-5 py-2 h-10 text-[14px] font-semibold border-0"
            data-testid="cookie-modal-save"
          >
            Save Preferences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
